import React, { useState, useEffect, useId } from 'react';
import { useDispatch } from 'react-redux';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import { CompactPicker } from 'react-color';
import { AutosaveLinkifiedTextarea, ClickOutside, DatePicker } from '@fikasio/react-ui-components';
import ParentProjectField from './ParentProjectField';
import { clearPaneContent } from 'services/pane/slice';
import {
  useGetProjectsQuery,
  useAddProjectMutation,
  usePatchProjectMutation,
  useUpdateProjectMutation,
} from 'services/projects/api';
import { Project, ProjectMember } from 'services/projects/types';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import { statusChangePatch } from 'components/tasks/TasksViewer/statusChange';
import ProjectStatusDropdown from 'components/projects/ProjectStatusDropdown';
import { invertColor } from 'utils/colors';
import { pickedDateTimeInUserZone, toJSDate, toPickerDate } from 'utils/date';
import { canChooseParentProject, eligibleParentProjects, projectStatus } from 'components/projects/utils';
import { preserveEmptyString } from 'utils/forms';
import 'components/UI/Form.css';

interface ProjectInformationsFormProps {
  creatingRef?: { current: Promise<Project | undefined> | null };
  editedMembers?: ProjectMember[];
  nameRef?: { current: string };
  project?: Project;
}

const withoutMembers = (values: object) => (
  Object.fromEntries(
    Object.entries(values).filter(([key]) => key !== 'members'),
  )
);

const ProjectInformationsForm = ({
  creatingRef,
  editedMembers,
  nameRef,
  project,
}: ProjectInformationsFormProps) => {
  const descriptionId = useId();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: projects } = useGetProjectsQuery();
  const [startAt, setStartAt] = useState(
    toJSDate(project?.startAt) ?? null,
  );
  const [dueAt, setDueAt] = useState(
    toJSDate(project?.dueAt) ?? null,
  );
  const [color, setColor] = useState(project?.color);
  const [isColorpickerOpen, setIsColorpickerOpen] = useState(false);

  const [createProject] = useAddProjectMutation();
  const [patchProject] = usePatchProjectMutation();
  const [updateProject] = useUpdateProjectMutation();

  useEffect(() => {
    setStartAt(toJSDate(project?.startAt) ?? null);
    setDueAt(toJSDate(project?.dueAt) ?? null);
  }, [project?.id, project?.startAt, project?.dueAt]);

  const canSetParent = canChooseParentProject(project, projects || []);
  const parentProjects = eligibleParentProjects(projects || [], project)
    .map(item => ({
      id: item.id,
      name: item.name,
    }));

  const onSubmit = async values => {
    const formData = {
      ...withoutMembers(values),
      assignee: values.assignee || null,
      ...(canSetParent ? { parentProject: values.parentProject || null } : {}),
      ...(nameRef ? { name: nameRef.current } : {}),
      ...(editedMembers !== undefined ? { members: editedMembers } : {}),
    };
    const saveProject = (projectId: string) => updateProject({
      id: projectId,
      ...formData,
    }).then(() => dispatch(clearPaneContent()));

    if (project?.id) {
      saveProject(project.id);
      return;
    }
    const pending = creatingRef?.current;
    if (pending) {
      const created = await pending;
      if (created?.id) {
        saveProject(created.id);
      }
      return;
    }
    createProject(formData)
      .then(() => dispatch(clearPaneContent()));
  };

  const status = projectStatus(project);

  return (
    <Form
      initialValues={project ? withoutMembers(project) : undefined}
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <div>
            <label className="form-label" htmlFor={descriptionId}>{t('description')}</label>
            <Field
              name="description"
              parse={preserveEmptyString}
            >
              {({ input }) => (
                <AutosaveLinkifiedTextarea
                  id={descriptionId}
                  name="description"
                  onBlur={input.onBlur}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onSave={description => {
                    if (project?.id) {
                      patchProject({
                        description,
                        id: project.id,
                      });
                    }
                  }}
                  value={input.value ?? ''}
                />
              )}
            </Field>
          </div>
          {
            canSetParent && (
              <div>
                <label className="form-label">{t('parentProject')}</label>
                <Field name="parentProject">
                  {({ input }) => (
                    <ParentProjectField
                      onChange={input.onChange}
                      projects={parentProjects}
                      value={input.value || null}
                    />
                  )}
                </Field>
              </div>
            )
          }
          <div>
            <label className="form-label">{t('color')}</label>
            <br />
            <Field
              name="color"
            >
              {
                ({ input }) => (
                  <>
                    <input
                      name="color"
                      type="hidden"
                      value={color}
                    />
                    <Button
                      onClick={() => setIsColorpickerOpen(true)}
                      style={{
                        backgroundColor: color,
                        borderColor: color,
                        color: color && invertColor(color),
                      }}
                      variant={!color ? 'link' : ''}
                    >
                      {t('select')}
                    </Button>
                    {
                      isColorpickerOpen && (
                        <ClickOutside
                          onClickOutside={() => setIsColorpickerOpen(false)}
                        >
                          <CompactPicker
                            color={color}
                            onChangeComplete={c => {
                              setColor(c.hex);
                              input.onChange(c.hex);
                              setIsColorpickerOpen(false);
                            }}
                          />
                        </ClickOutside>
                      )
                    }
                  </>
                )
              }
            </Field>
          </div>
          <div>
            <label className="form-label">{t('startDate')}</label>
            <br />
            <Field
              component={
                ({ input }) => (
                  <DatePicker
                    className="form-control"
                    defaultValue={toPickerDate(startAt)}
                    displayFormat="yyyy-MM-dd"
                    key={startAt ? 'has-start-at' : 'no-start-at'}
                    name="startAt"
                    onChange={value => {
                      const timestamp = pickedDateTimeInUserZone(value)
                        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                      if (project?.id) {
                        patchProject({
                          id: project.id,
                          startAt: timestamp.toJSDate(),
                        });
                      }
                      setStartAt(timestamp.toJSDate());
                      input.onChange(timestamp.toJSDate());
                    }}
                    onRemoveValue={e => {
                      e.stopPropagation();
                      if (project?.id) {
                        patchProject({
                          id: project.id,
                          startAt: null,
                        });
                      }
                      setStartAt(null);
                      input.onChange(null);
                    }}
                    showRemoveValue
                    showTimeSelect={false}
                    value={toPickerDate(startAt)}
                  />
                )
              }
              defaultValue={startAt ?? undefined}
              name="startAt"
            />
          </div>
          <br />
          <div
            className="projectRow_dueAt"
          >
            <label className="form-label">{t('deadline')}</label>
            <br />
            <Field
              component={
                ({ input }) => (
                  <DatePicker
                    className="form-control"
                    defaultValue={toPickerDate(dueAt)}
                    displayFormat="yyyy-MM-dd"
                    key={dueAt ? 'has-due-at' : 'no-due-at'}
                    name="dueAt"
                    onChange={val => {
                      const timestamp = pickedDateTimeInUserZone(val)
                        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                      if (project && project.id) {
                        patchProject({
                          id: project.id,
                          dueAt: timestamp.toJSDate(),
                        });
                      }
                      setDueAt(timestamp.toJSDate());
                      input.onChange(timestamp.toJSDate());
                    }}
                    onRemoveValue={e => {
                      e.stopPropagation();
                      if (project?.id) {
                        patchProject({
                          id: project.id,
                          dueAt: null,
                        });
                      }
                      setDueAt(null);
                      input.onChange(null);
                    }}
                    showRemoveValue
                    showTimeSelect={false}
                    value={toPickerDate(dueAt)}
                  />
                )
              }
              defaultValue={dueAt ?? undefined}
              name="dueAt"
            />
          </div>
          <br />
          <div>
            <label className="form-label">{t('assignee')}</label>
            <Field name="assignee">
              {({ input }) => (
                <AssigneeButton
                  assigneeId={input.value || undefined}
                  onChange={assignee => {
                    input.onChange(assignee);
                    if (project?.id) {
                      patchProject({
                        assignee,
                        id: project.id,
                      });
                    }
                  }}
                />
              )}
            </Field>
          </div>
          {
            project?.id && (
              <div>
                <label className="form-label">{t('status')}</label>
                <div>
                  <ProjectStatusDropdown
                    alignToggle="start"
                    onChange={(nextStatus, extras) => {
                      patchProject({
                        id: project.id,
                        ...statusChangePatch(nextStatus, {
                          cancelReason: extras?.cancelReason,
                          wasArchived: project.isArchived,
                        }),
                      });
                    }}
                    project={project}
                    status={status}
                  />
                </div>
                {
                  status === 'Cancelled' && project.cancelReason && (
                    <div
                      style={{
                        color: '#6c757d',
                        marginTop: 8,
                      }}
                    >
                      {t('cancelReason')}: {project.cancelReason}
                    </div>
                  )
                }
              </div>
            )
          }
          <div
            style={{
              float: 'right',
              bottom: 10,
              paddingBottom: 15,
              position: 'absolute',
              right: 30,
            }}
          >
            {
              project
                ? <Button type="submit" variant="success">{t('save')}</Button>
                : <Button type="submit" variant="success">{t('create')}</Button>
            }
          </div>
        </form>
      )}
    />
  );
};

export default ProjectInformationsForm;
